import { APIComponentAttributes } from '@ardoq/api-types';
import { Column } from 'ag-grid-enterprise';
import { WorkspaceModelAndTypeIds } from '../types';
import { createComponentsOperations } from './createComponentsOperations';
import { toComponentNameFromClipboardText } from '../valueHandlers';

const splitValuesFromText = (text: string): string[][] => {
  if (!text) {
    return [];
  }

  const lines = text.split('\n');
  return lines.map(line => {
    if (line.includes('\t')) {
      return line.split('\t');
    }
    if (line.includes(',')) {
      return parseCSVLine(line);
    }
    return [line];
  });
};

const parseCSVLine = (line: string): string[] => {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      // Handle escaped quote: "" becomes "
      current += '"';
      i++; // Skip the next quote
    } else if (char === '"') {
      // Toggle quote state
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

const applyValuesToRelevantColumns = (
  cellValues: string[][],
  columns: Column[],
  defaultComponentProps: Partial<APIComponentAttributes>
): Partial<APIComponentAttributes>[] => {
  return cellValues.map(row => {
    const component: Partial<APIComponentAttributes> = {
      ...defaultComponentProps,
      _id: createComponentsOperations.getComponentTemporaryId(), // AG grid requires unique IDs. They will be replaced by the backend on save
    };
    row.forEach((value: any, index: number) => {
      if (columns[index]) {
        const field = columns[index].getColDef().field;
        if (field === 'name') {
          component.name = toComponentNameFromClipboardText(value);
        } else if (field) {
          component[field] = value;
        }
      }
    });
    return component;
  });
};

export const parseClipboardTextIntoComponents = (
  clipboardText: string,
  workspaceIdModelIdAndComponentTypeId: WorkspaceModelAndTypeIds,
  columns: Column[]
): Partial<APIComponentAttributes>[] => {
  const textInRows = splitValuesFromText(clipboardText);
  if (textInRows.length === 0) {
    return [];
  }

  const defaultComponentProps = {
    rootWorkspace: workspaceIdModelIdAndComponentTypeId.workspaceId,
    typeId: workspaceIdModelIdAndComponentTypeId.componentTypeId,
    model: workspaceIdModelIdAndComponentTypeId.modelId,
    name: '',
  };

  return applyValuesToRelevantColumns(
    textInRows,
    columns,
    defaultComponentProps
  );
};
